import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { recommendationData } from "../../ProjectPage/components/ProjectInformation";

interface CarbonSequestrationChartProps {
  recommendationProjectData: recommendationData;
}

const CarbonSequestrationChart: React.FC<CarbonSequestrationChartProps> = ({
  recommendationProjectData,
}) => {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend,
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: "Years",
        },
      },
      y: {
        title: {
          display: true,
          text: "CO2 sequestration (MtCO2e)",
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
  };

  const years = Array.from({ length: 41 }, (_, i) => i);

  const data = {
    labels: years,
    datasets: [
      {
        fill: true,
        label: "Observed sequestration per hectare from past carbon projects",
        data: years.map((year) => Math.min(14, 0.2 * Math.exp(0.1 * year))),
        borderColor: "rgba(99, 102, 241, 1)",
        backgroundColor: "rgba(99, 102, 241, 0.2)",
      },
      {
        label: "Biomass data based sequestration per hectare",
        data: years.map((year) => 0.15 * year + 0.5),
        borderColor: "rgba(244, 63, 94, 1)",
        backgroundColor: "rgba(244, 63, 94, 0.2)",
      },
      {
        label: "Actual Carbon Credits Yielded from past projects",
        data: years.map((year) => 0.1 * year + 0.3),
        borderColor: "rgba(75, 85, 99, 1)",
        borderWidth: 2,
      },
      {
        label: "Estimated sequestration (ProClime)",
        data: years.map((year) => {
          const biomass = recommendationProjectData?.sumOfAboveGroundBiomassTonnes || 0;
          const soil = recommendationProjectData?.sumOfSoilOrganicCarbonTonnes || 0;
          return ((biomass + soil) * (1 + 0.08 * year)) / 1000000; // Convert to MtCO2e
        }),
        borderColor: "rgba(34, 197, 94, 1)",
        borderWidth: 8,
      },
    ],
  };

  const baselineData = {
    labels: years,
    datasets: [
      {
        label: "Baseline (Without Project)",
        data: years.map((year) => year * 0.05),
        borderColor: "rgba(156, 163, 175, 1)",
        borderWidth: 2,
      },
      {
        label: "With Project (Additionality)",
        data: years.map((year) => {
          const biomass = recommendationProjectData?.sumOfAboveGroundBiomassTonnes || 0;
          const soil = recommendationProjectData?.sumOfSoilOrganicCarbonTonnes || 0;
          return ((biomass + soil) * (1 + 0.08 * year)) / 1000000;
        }),
        borderColor: "rgba(34, 197, 94, 1)",
        borderWidth: 8,
      },
      {
        label: "Leakage (-12%)",
        data: years.map((year) => {
          const biomass = recommendationProjectData?.sumOfAboveGroundBiomassTonnes || 0;
          const soil = recommendationProjectData?.sumOfSoilOrganicCarbonTonnes || 0;
          return ((biomass + soil) * (1 + 0.08 * year) * 0.88) / 1000000;
        }),
        borderColor: "rgba(244, 63, 94, 1)",
        borderDash: [5, 5],
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-full rounded-lg bg-white shadow-md">
        <h3 className="p-4 text-lg font-medium">Carbon Sequestration Analysis</h3>
        <div className="h-[0.5px] w-full bg-gray-300"></div>
        <div className="h-[500px] p-4">
          <Line options={options} data={data} />
        </div>
      </div>

      <div className="max-w-full rounded-lg bg-white shadow-md">
        <h3 className="p-4 text-lg font-medium">Project Impact Analysis</h3>
        <div className="h-[0.5px] w-full bg-gray-300"></div>
        <div className="h-[500px] p-4">
          <Line
            options={{
              ...options,
              plugins: {
                ...options.plugins,
                title: { display: true, text: "Project Carbon Analysis with Baseline and Leakage" },
              },
            }}
            data={baselineData}
          />
        </div>
      </div>
    </div>
  );
};

export default CarbonSequestrationChart;
